import { computed } from "vue";
import { resolvePublicR2Url } from "@/utils/r2Url";

export function resolveEffectMediaFields(effect) {
  if (!effect || typeof effect !== "object") return effect;
  return {
    ...effect,
    cover_url: resolvePublicR2Url(effect.cover_url),
    cover_video: resolvePublicR2Url(effect.cover_video),
    cover_gif: resolvePublicR2Url(effect.cover_gif),
    merge_bgm_mp3: resolvePublicR2Url(effect.merge_bgm_mp3),
  };
}

export function resolveModelAvatarFields(model) {
  if (!model || typeof model !== "object") return model;
  const avatar = model.avatar;
  if (!avatar || typeof avatar !== "object") return model;
  return {
    ...model,
    avatar: {
      ...avatar,
      src: resolvePublicR2Url(avatar.src),
    },
  };
}

function normalizeModelVersionList(models) {
  return Array.isArray(models) ? models.map(resolveModelAvatarFields) : models;
}

function normalizeModelTitleGroups(groups) {
  if (!groups || typeof groups !== "object") return groups;
  if (Array.isArray(groups)) return normalizeModelVersionList(groups);
  return Object.fromEntries(
    Object.entries(groups).map(([title, models]) => [
      title,
      normalizeModelVersionList(models),
    ])
  );
}

export function normalizeModelsByTool(data) {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data)) return normalizeModelVersionList(data);
  return Object.fromEntries(
    Object.entries(data).map(([toolName, groups]) => [
      toolName,
      normalizeModelTitleGroups(groups),
    ])
  );
}

/**
 * 将 effects index 响应统一为数组（与任务页 / 产品页约定一致）
 */
export function normalizeEffectsList(data) {
  if (data == null) return [];
  try {
    if (typeof data === "string") {
      return normalizeEffectsList(JSON.parse(data));
    }
    if (Array.isArray(data)) return data.map(resolveEffectMediaFields);
    if (typeof data === "object") {
      return Object.values(data).flat().map(resolveEffectMediaFields);
    }
  } catch (e) {
    console.error("normalizeEffectsList 失败:", e);
  }
  return [];
}

export const normalizeEffectsIndexList = normalizeEffectsList;

export function useAiR2Models() {
  const r2CachedFetch = useR2CachedFetch();

  const { data: modelsByTool } = useAsyncData(
    "fetchModels",
    () => r2CachedFetch("/api/r2/ai/models.json"),
    {
      default: () => ({}),
      transform: normalizeModelsByTool,
    }
  );

  return { modelsByTool };
}

/**
 * 通过 useRequestFetch + useAsyncData 拉取 R2 上的 models / effects index 配置。
 *
 * 必须同步调用多个 useAsyncData（不要在中间 await），否则会脱离 Nuxt/Vue setup 上下文，
 * 触发 “[nuxt] instance unavailable”。
 */
export function useAiR2ModelsAndEffects() {
  const r2CachedFetch = useR2CachedFetch();

  const { modelsByTool } = useAiR2Models();

  const { data: videoEffectsRaw } = useAsyncData(
    "ai-video-effects-index",
    () => r2CachedFetch("/api/r2/ai/effects/video/index.json"),
    {
      default: () => null,
      transform: (data) => data,
    }
  );

  const { data: imageEffectsRaw } = useAsyncData(
    "ai-image-effects-index",
    () => r2CachedFetch("/api/r2/ai/effects/image/index.json"),
    {
      default: () => null,
      transform: (data) => data,
    }
  );

  const effectsByTool = computed(() => ({
    video: normalizeEffectsList(videoEffectsRaw.value),
    image: normalizeEffectsList(imageEffectsRaw.value),
  }));

  return {
    modelsByTool,
    effectsByTool,
  };
}
